/*
 * Authors: mariushirjanu, triadiprabowo
 * Created: 07/10/2021
 */
import _ from 'underscore';
import { UntypedFormGroup } from '@angular/forms';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { IRoleContact, IWebshopInfo, IWebshopInfoData, WebshopInfoDataService } from './webshop-info-data.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { GaStateService } from '@app/shared/services/ga-state.service';
import { WebshopInfoService } from './webshop-info.service';
import { CompanyDataService, CompanyType, ICodeDescription, ICompanyCustomerInfo } from '@app/shared/company/company-data.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { LovService } from '@app/shared/services/lov.service';
import { GaValidateService, IValidatingComponent } from '@app/shared/formly/ga-validate.service';
import { WebshopDataService } from '../webshop-data.service';
import { SEC_WS_INFO, SEC_WS_INFO_ACCOUNT_MANAGEMENT, SEC_WS_INFO_GBDM, SEC_WS_INFO_LAMSIGNUP, SEC_WS_INFO_LAMTYPE, SEC_WS_INFO_WS_NAME_AND_TYPE } from '@app/shared/services/auth/security-objects';
import { ICompany, ICompanyCountry, IContact, ICountryExtended, IGenericUser, ILabelledValue, ILocation, ILov, IObject, IValueObject } from '@type_definitions/datatypes';
import { util } from '@app/shared/util';
import { IUserData } from '@app/shared/services/auth/auth-data.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { WebshopService } from '../webshop.service';
import { DialogSize, GaMessageService, IDialog } from '@app/shared/message/ga-message.service';
import { ListContactsComponent } from '@app/shared/contact/list-contacts.component';
import { UserProfileWrapperComponent } from '@app/main/userprofile/user-profile-wrapper.component';
import { FaqPopupComponent } from '@app/shared/faq/faq-popup.component';
import { ImageUploadComponent } from '@app/shared/file-upload/image-upload.component';
import { GaWizard } from '@app/shared/wizard/ga-wizard';
import { GaWizardService } from '@app/shared/wizard/ga-wizard.service';
import { WIZARD_WEBSHOP } from '@app/shared/app-wizards';
import { FAQ_ORDER_MANAGEMENT_TYPES } from '@app/shared/faq/faq-codes';
import { RoleContactType } from '@app/shared/contact/role-contact-type';

interface ICollapsibleWebshopCategory {
  fieldRef:FieldRef;
  fields:IExtraFieldConfigurationObject[];
  title:string;
  editableKey:string;
  collapsedInfo:{
    title?:string;
    description?:string;
    contacts?:IContact[];
    roleContacts?:IRoleContact[];
    orderUpdateContacts?:IContact[];
    orderUpdateRoleContacts?:IRoleContact[];
    testMode?:boolean;
    hasLogo?:boolean;
    logoUrl?:string;
  };
  collapsed:boolean;
}

interface IFormClass {
  leftColumn:string;
  rightColumn:string;
  halfColumn?:string;
  fullColumn?:string;
}

const SAVE_INFO_ACTION:string = 'SAVE_WEBSHOP_INFO';
const ORDER_UPDATE_CONTACTS_KEY:string = 'orderUpdateContacts';

export enum FieldRef {
  ACCOUNT_MANAGEMENT = 'accountManagement',
  ACSL = 'acsl',
  ASSESSMENT = 'assessment',
  CATALOG = 'catalogInfo',
  CUSTOMER = 'customer',
  LAM = 'lam',
  OEM = 'oem',
  WEBSHOP = 'webshop',
  WEBSHOP_STATUS = 'webshopStatus'
}

export enum FieldColumn {
  LEFT,
  RIGHT
}

@Component({
  selector: 'webshop-info',
  template: `
    <div class="webshop-info row" *ngIf="!loading">

      <!-- categories -->
      <div class="col-md-6" *ngFor="let column of formSections"> <!-- repeat the column -->
        <div class="col-space-bottom" *ngFor="let category of column"> <!-- repeat data column -->
          <editable-text [key]="category.editableKey" category="WEBSHOP" screen="WEBSHOP" menu="title,info" highlight="h3 a:eq(0)"></editable-text>
          <collapsible-pane [title]="category.title" [collapsed]="category.collapsed" (onChange)="onCategoryCollapsed(category, $event)">
            <div class="form-horizontal section-content" [attr.data-ref]="category.title"> <!-- collapsed fields -->
              <formly [form]="form" [fields]="category.fields" [model]="model"></formly>
            </div>
          </collapsible-pane>

          <div class="section-content" [attr.data-ref]="category.title" *ngIf="category.collapsed && category.collapsedInfo">
            <p>{{category.collapsedInfo.title}}</p>
            <div class="alert alert-warning" *ngIf="category.collapsedInfo.testMode">
              <i class="fa fa-warning"></i>&nbsp;
              <text class="d-inline-block" key="TEST_MODE"></text>
            </div>
            <p *ngIf="category.collapsedInfo.description" [class]="(category.fieldRef === '${FieldRef.WEBSHOP}' || category.fieldRef === '${FieldRef.WEBSHOP_STATUS}') ? 'pre-line' : ''" [innerHTML]="category.collapsedInfo.description"></p>
            <p *ngIf="category.fieldRef === '${FieldRef.ACCOUNT_MANAGEMENT}'">{{model.focusContactType?.value}}</p>
            <div *ngIf="category.collapsedInfo.roleContacts">
              <p *ngFor="let roleContact of category.collapsedInfo.roleContacts">
                <contact-info [roleContact]="roleContact"></contact-info>
              </p>
            </div>
            <div *ngIf="category.collapsedInfo.contacts">
              <p *ngFor="let contact of category.collapsedInfo.contacts">
                <contact-info [contact]="contact"></contact-info>
              </p>
            </div>
            <img *ngIf="category.collapsedInfo.hasLogo" [src]="category.collapsedInfo.logoUrl" alt="logo" style="max-width: 100%; max-height: 100px;"/>
          </div>
        </div>
      </div>
    </div>
    <h3 *ngIf="loading">
      <activity-indicator useDefaultText="true"></activity-indicator>
    </h3>
  `,
  styleUrls: [ './webshop-info.scss' ],
  encapsulation: ViewEncapsulation.None
})
export class WebshopInfoComponent implements IValidatingComponent, OnInit, OnDestroy {
  public formSections:ICollapsibleWebshopCategory[][];
  public form:UntypedFormGroup = new UntypedFormGroup({});

  public model:IWebshopInfo;
  public loading:boolean;
  private webshopInfoData:IWebshopInfoData;
  private userProfileDialog:IDialog;

  private lamTypeField:IExtraFieldConfigurationObject;
  private lamCompanyCountryField:IExtraFieldConfigurationObject;
  private lamCompanyDepartmentField:IExtraFieldConfigurationObject;
  private lamCustomerCountryField:IExtraFieldConfigurationObject;
  private lamMainContactsField:IExtraFieldConfigurationObject;
  private lamMainContactsInfoField:IExtraFieldConfigurationObject;
  private gbdmContactField:IExtraFieldConfigurationObject;
  private gbdmContactInfoField:IExtraFieldConfigurationObject;
  private oemMainContactsField:IExtraFieldConfigurationObject;
  private oemMainContactsInfoField:IExtraFieldConfigurationObject;
  private webshopTypeField:IExtraFieldConfigurationObject;
  private orderProcessField:IExtraFieldConfigurationObject;
  private webshopStatusField:IExtraFieldConfigurationObject;
  private customerRelationField:IExtraFieldConfigurationObject;
  private opportunityTypeField:IExtraFieldConfigurationObject;
  private winChancesField:IExtraFieldConfigurationObject;
  private customerIdOemField:IExtraFieldConfigurationObject;
  private oemCustomerTypeField:IExtraFieldConfigurationObject;
  private oemField:IExtraFieldConfigurationObject;
  private companyHQField:IExtraFieldConfigurationObject;
  private customerMainContactInfoField:IExtraFieldConfigurationObject;
  private catalogUploadDateField:IExtraFieldConfigurationObject;
  private userUploadDateField:IExtraFieldConfigurationObject;
  private customerSectorField:IExtraFieldConfigurationObject;
  private focusContactTypeField:IExtraFieldConfigurationObject;
  private focusContactInfoField:IExtraFieldConfigurationObject;
  private amTypeField:IExtraFieldConfigurationObject;
  private orderManagementTypeField:IExtraFieldConfigurationObject;
  private orderUpdateContactsField:IExtraFieldConfigurationObject;
  private orderUpdateContactsInfoField:IExtraFieldConfigurationObject;
  private wizard:GaWizard;

  private hasWritePermission:boolean;
  private canUpdateLamType:boolean;
  private canViewLamType:boolean;
  private canUpdateGBDM:boolean;
  private canUpdateAccountManagement:boolean;
  private canCreateLam:boolean;
  private canUpdateWebshopNameAndType:boolean;
  private focusContacts:IRoleContact[];

  private readonly labelClass:IFormClass = {
    leftColumn: 'col-lg-5 col-md-6',
    rightColumn: 'col-lg-5 col-md-5'
  };

  private readonly valueClass:IFormClass = {
    leftColumn: 'col-lg-7 col-md-6',
    rightColumn: 'col-lg-7 col-md-7',
    halfColumn: 'col-lg-4 col-md-6'
  };

  private readonly fullColumnClass:string = 'col-sm-12';

  constructor(
    private webshopInfoDataService:WebshopInfoDataService,
    private translationService:TranslationService,
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private gaStateService:GaStateService,
    private webshopService:WebshopService,
    private webshopInfoService:WebshopInfoService,
    private companyDataService:CompanyDataService,
    private authService:AuthService,
    private lovService:LovService,
    private messageService:GaMessageService,
    private validateService:GaValidateService,
    private wizardService:GaWizardService,
    private webshopDataService:WebshopDataService
  ) {
    this.wizard = this.wizardService.getInstance(WIZARD_WEBSHOP);
  }

  public ngOnInit():void {
    // get permission of webshop info
    this.getPermissions();

    // register wizard actions and validator
    this.initWizardSetup();

    this.gaStateService.screen = 'WEBSHOP.WEBSHOP_INFO'; // point to the correct screen for loading the correct translations in formly

    // initial webshop info data
    this.loading = true;
    this.webshopInfoDataService.getWebshopInfo(this.webshopService.getWebshopId())
      .then((res:IWebshopInfoData) => {
        this.webshopInfoData = res;

        // init formly model
        this.model = this.webshopInfoService.createWebshopInfoModel(this.webshopInfoData);

        // init form collapsible categories
        this.initCategories();

        // fill option fields
        this.fillFields(this.lamType);

        if (this.hasWritePermission) {
          // after initial model has been set, we load necessary lists
          this.getOemCustomerTypes(this.webshopInfoData?.oemInfo?.company?.id);

          // initial on change field
          this.onCatalogAvailableChange({
            value: this.webshopInfoData?.catalogInfo?.oemCatalogAvailable,
            label: ''
          });
          this.onUserAddressListAvailableChange({
            value: this.webshopInfoData?.opportunityAssessment?.userListAvailable,
            label: ''
          });
          this.onMainOEMChange(this.webshopInfoData?.oemInfo?.mainOem);
        }

        // initial on change field (all access)
        this.onCustomerMainContactChange(this.webshopInfoData?.customerInfo?.mainContact?.contact);
        this.onGBDMContactChange(this.webshopInfoData?.gbdmInfo?.webshopContact?.contact);
        this.onOrderUpdateContactsChange();
        this.onLAMMainContactsChange();
        this.onOEMMainContactsChange();

        // initial loading of LAM details
        this.onLAMChange();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private initCategories():void {
    this.formSections = [
      [
        {
          fieldRef: FieldRef.CUSTOMER,
          fields: this.formSections?.[ FieldColumn.LEFT ]?.[ 0 ]?.fields || this.createCustomerFields(),
          title: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.CUSTOMER.TITLE'),
          editableKey: 'WEBSHOP_INFO.CUSTOMER',
          collapsedInfo: {
            title: this.model.customerCompany,
            contacts: this.model.customerMainContact? [ this.model.customerMainContact ] : [],
            hasLogo: this.webshopInfoData.customerInfo.hasCustomerLogo,
            logoUrl: this.getCustomerLogoUrl()
          },
          collapsed: true
        },
        {
          fieldRef: FieldRef.WEBSHOP,
          fields: this.formSections?.[ FieldColumn.LEFT ]?.[ 1 ]?.fields || this.createWebshopFields(),
          title: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.WEBSHOP.TITLE'),
          editableKey: 'WEBSHOP_INFO.WEBSHOP',
          collapsedInfo: {
            title: this.model.webshopName,
            description: this.model.webshopDescription,
            testMode: !this.webshopInfoData.webshop.inProduction,
            hasLogo: this.webshopInfoData.customerInfo.hasWebshopLogo,
            logoUrl: this.getWebshopLogoUrl()
          },
          collapsed: true
        },
        {
          fieldRef: FieldRef.ASSESSMENT,
          fields: this.formSections?.[ FieldColumn.LEFT ]?.[ 2 ]?.fields || this.createAssessmentFields(),
          title: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.ASSESSMENT.TITLE'),
          editableKey: 'WEBSHOP_INFO.ASSESSMENT',
          collapsedInfo: {
            description: this.getAssessmentCollapsedInfo()
          },
          collapsed: true
        },
        {
          fieldRef: FieldRef.ACCOUNT_MANAGEMENT,
          fields: this.formSections?.[ FieldColumn.LEFT ]?.[ 3 ]?.fields || this.createAccountManagementFields(),
          title: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.ACCOUNT_MANAGEMENT.TITLE'),
          editableKey: 'WEBSHOP_INFO.ACCOUNT_MANAGEMENT',
          collapsedInfo: {
            orderUpdateRoleContacts: this.model.orderUpdateRoleContacts
          },
          collapsed: true
        }
      ],
      [
        {
          fieldRef: FieldRef.WEBSHOP_STATUS,
          fields: this.formSections?.[ FieldColumn.RIGHT ]?.[ 0 ]?.fields || this.createWebshopStatusFields(),
          title: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.WEBSHOP_STATUS.TITLE'),
          editableKey: 'WEBSHOP_INFO.WEBSHOP_STATUS',
          collapsedInfo: {
            title: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.STATUS_INFORMATION',
              {
                status: this.model.webshopStatus?.value?.toUpperCase(),
                date: this.formattingService.formatDate(this.model.webshopStatusDate, 'DD MMM YYYY')
              }),
            description: this.model.webshopStatusComments
          },
          collapsed: true
        },
        {
          fieldRef: FieldRef.OEM,
          fields: this.formSections?.[ FieldColumn.RIGHT ]?.[ 1 ]?.fields || this.createOEMFields(),
          title: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.OEM.TITLE'),
          editableKey: 'WEBSHOP_INFO.OEM',
          collapsedInfo: {
            title: this.model.oem?.name,
            roleContacts: this.model.oemRoleContacts
          },
          collapsed: true
        },
        {
          fieldRef: FieldRef.CATALOG,
          fields: this.formSections?.[FieldColumn.RIGHT]?.[2]?.fields || this.createCatalogFields(),
          title: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.CATALOG.TITLE'),
          editableKey: 'WEBSHOP_INFO.CATALOG',
          collapsedInfo: {
            description: this.getCatalogCollapsedInfo()
          },
          collapsed: true
        },
        {
          fieldRef: FieldRef.LAM,
          fields: this.formSections?.[ FieldColumn.RIGHT ]?.[ 3 ]?.fields || this.createLAMFields(),
          title: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.LAM.TITLE'),
          editableKey: 'WEBSHOP_INFO.LAM',
          collapsedInfo: {
            title: this.lamType === 'V'? this.model.lamCompanyDepartment?.company?.name : this.model.lamCompanyCountry?.company?.name,
            roleContacts: this.model.lamRoleContacts,
            description: this.model.lamComments
          },
          collapsed: true
        },
        {
          fieldRef: FieldRef.ACSL,
          fields: this.formSections?.[ FieldColumn.RIGHT ]?.[ 4 ]?.fields || this.createACSLFields(),
          title: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.ACSL.TITLE'),
          editableKey: 'WEBSHOP_INFO.ACSL',
          collapsedInfo: {
            contacts: this.model.gbdm? [ this.model.gbdm ] : []
          },
          collapsed: true
        }
      ]
    ];
  }

  private getAssessmentCollapsedInfo():string {
    return `${this.model.expectedOrdersPerYear? '<p>' + this.model.expectedOrdersPerYear + ' ' + this.translationService.instant('WEBSHOP.WEBSHOP_INFO.EXPECTEDORDERSPERYEAR.LABEL') + '</p>' : ''}
		${this.model.revenueForecast? '<p>' + this.formattingService.formatAmount(this.model.revenueForecast, 'USD') + ' ' + this.translationService.instant('WEBSHOP.WEBSHOP_INFO.REVENUEFORECAST.LABEL') + '</p>' : ''}`;
  }

  private getCatalogCollapsedInfo(): string {
    return `
      ${util.notNullOrUndefinedOrEmpty(this.model?.oemCatalogAvailable) ? `<p>${this.translationService.instant('WEBSHOP.WEBSHOP_INFO.OEMCATALOGAVAILABLE.LABEL')}: ${this.formattingService.formatLabel(this.model?.oemCatalogAvailable)}</p>` : ''}
      ${util.notNullOrUndefinedOrEmpty(this.model?.bpCatalogActivatedQty) ? `<p>${this.translationService.instant('WEBSHOP.WEBSHOP_INFO.BPCATALOGACTIVATEDQTY.LABEL')}: ${this.model?.bpCatalogActivatedQty}</p>` : ''}
    `;
  }

  private initWizardSetup():void {
    // register current validator to use validateCurrent() when saving
    this.wizard.currentValidator(this.validate, true);

    if (this.hasWritePermission) {
      this.wizard.addAction(
        this.wizardService.createActionButton(
          SAVE_INFO_ACTION,
          this.translationService.instant('GA.BUTTONS.SAVE_LABEL', { label: this.translationService.instant('WEBSHOP.TABS.WEBSHOP_INFO') }),
          'btn btn-success',
          this.saveWebshopInfo
        )
      );
    }
  }

  private getPermissions():void {
    this.hasWritePermission = this.authService.viewWrite(SEC_WS_INFO);

    this.canUpdateLamType = this.authService.viewWrite(SEC_WS_INFO_LAMTYPE);
    this.canViewLamType = this.authService.hasViewPermission(SEC_WS_INFO_LAMTYPE);

    this.canUpdateGBDM = this.authService.viewWrite(SEC_WS_INFO_GBDM);
    this.canUpdateAccountManagement = this.authService.viewWrite(SEC_WS_INFO_ACCOUNT_MANAGEMENT);
    this.canCreateLam = this.authService.viewWrite(SEC_WS_INFO_LAMSIGNUP);

    this.canUpdateWebshopNameAndType = this.authService.viewWrite(SEC_WS_INFO_WS_NAME_AND_TYPE);
  }

  private fillFields(lamType:string):void {
    if (this.hasWritePermission) {

      const companyId:number = this.webshopInfoData.customerInfo?.company?.id;

      if (this.canUpdateGBDM) {
        this.formlyService.fillField(this.gbdmContactField, this.companyDataService.getCompanyGBDMs());
      }

      this.formlyService.fillField(this.webshopTypeField, this.lovService.list('DOPROTY'));
      this.formlyService.fillField(this.orderProcessField, this.lovService.list('DOCENLO'));
      this.formlyService.fillField(this.webshopStatusField, this.lovService.list('DOSPRST'));
      this.formlyService.fillField(this.customerRelationField, this.lovService.list('DOCOMST'));
      this.formlyService.fillField(this.customerSectorField, this.companyDataService.getCompanySectors());

      this.formlyService.fillField(this.opportunityTypeField, this.lovService.list('DOOPPOR'));
      this.formlyService.fillField(this.winChancesField, this.lovService.list('DOWINCH'));
      this.formlyService.fillField(this.companyHQField, this.companyDataService.getCompanyLocations(companyId));
      this.formlyService.fillField(this.orderManagementTypeField, this.lovService.list('DO_ORDER_MANAGEMENT_TYPE'));

      // get lam fields list
      switch (lamType) {
        case CompanyType.BP:
          this.formlyService.fillField(this.lamCompanyCountryField, this.companyDataService.getLAMCompanyCountries());
          break;
        case CompanyType.OEM:
          this.formlyService.fillField(this.lamCompanyDepartmentField, this.companyDataService.getLAMCompanyDepartments());
          break;
        case CompanyType.CUSTOMER:
          this.formlyService.fillField(this.lamCustomerCountryField, this.companyDataService.getLAMCustomerCountries(companyId));
          break;
        default:
          break;
      }

      if (this.canUpdateAccountManagement) {
        this.formlyService.fillField(this.amTypeField, this.lovService.list('DOAMTYPE'));
      }
    }

    // load all possible focus contacts and fill in focusContactType field
    this.loadFocusContact();
  }

  private loadFocusContact():void {
    this.webshopInfoDataService.getFocusContacts(this.webshopService.getWebshopId())
      .then((focusContacts:IRoleContact[]) => {
        this.focusContacts = focusContacts;
        if (this.hasWritePermission && this.canUpdateAccountManagement) {
          this.formlyService.fillField(this.focusContactTypeField, this.webshopInfoDataService.getFocusContactTypes());
        }
        return;
      })
      .then(() => {
        this.fillFocusContactField();
      });
  }

  private fillFocusContactField():void {
    this.model.focusContact = _.where(this.focusContacts, { contactType: this.model.focusContactType?.id });
    this.focusContactInfoField.templateOptions.inputs.roleContacts = this.model.focusContact;
    this.findCategoryByFieldRef(FieldColumn.LEFT, FieldRef.ACCOUNT_MANAGEMENT).collapsedInfo.roleContacts = this.model.focusContact;
  }

  private get lamType():string {
    return <string>this.model?.lamType?.id;
  }

  // preselect GBDM based on the LAM selection
  private setGBDMBasedOnLAM():void {
    const setGBDM = (gbdm:IContact):void => {
      if (!this.webshopInfoService.gbdm || (this.gbdmContactField?.formControl)?.dirty) { // set another GBDM only when there is no gbdm saved yet or the gbdm field was just changed
        this.model.gbdm = gbdm;
        this.onGBDMContactChange(gbdm);
      }
    };

    if (this.model.lamCompanyCountry) {
      setGBDM(this.model.lamCompanyCountry?.company?.gbdm); // gbdm of BP LAM company country
    } else if (this.model.lamCompanyDepartment) {
      const companyGBDM:IContact = this.model.lamCompanyDepartment?.company?.gbdm;
      setGBDM(this.model.lamCompanyDepartment.gbdm || companyGBDM); // gbdm of OEM LAM company department
    } else if (this.model.lamCustomerCountry) {
      this.webshopService.getLAM(this.model, this.webshopInfoData.customerInfo.company?.id)
        .then((lam:ICompanyCountry) => {
          setGBDM(lam?.company?.gbdm);
        });
    }
  }

  private lamCompanyCountriesPromise():Promise<ICompanyCountry[]> {
    return this.companyDataService.getLAMCompanyCountries()
      .then((lamCompanyCountries:ICompanyCountry[]) => {

        // auto-select LAM company country when there is only one
        if (lamCompanyCountries?.length === 1) {
          this.model.lamCompanyCountry = lamCompanyCountries[ 0 ];
        }

        return lamCompanyCountries;
      });
  }

  private lamCompanyDepartmentsPromise():Promise<ICompanyCountry[]> {
    return this.companyDataService.getLAMCompanyDepartments()
      .then((lamCompanyDepartments:ICompanyCountry[]) => {

        // If the logged-in user is an OEM user, pre-fill with the user's department if available. Otherwise, pre-fill with the department corresponding to the user's country.
        if (this.authService.checkVendorUser()) {
          this.model.lamCompanyDepartment = this.authService.getCurrentUser().companyDepartment;
          if (!this.model.lamCompanyDepartment) {
            return this.companyDataService.getUserCountryDepartment()
              .then((userCountryDepartment:ICompanyCountry) => {
                this.model.lamCompanyDepartment = userCountryDepartment;
                return lamCompanyDepartments;
              });
          }
        }
        // If the logged-in user is a non-OEM user, auto-select the LAM company department when there is only one.
        else if (lamCompanyDepartments?.length === 1) {
          this.model.lamCompanyDepartment = lamCompanyDepartments[ 0 ];
        }

        return lamCompanyDepartments;
      });
  }

  private lamCustomerCountriesPromise():Promise<ICountryExtended[]> {
    return this.companyDataService.getLAMCustomerCountries(this.webshopInfoData.customerInfo?.company?.id)
      .then((customerCountries:ICountryExtended[]) => {

        const countrycountryHeadQuarter:ICountryExtended = _.findWhere(customerCountries, { hasCountryHeadQuarter: true });
        if (countrycountryHeadQuarter) {
          // set to the country of the customer's HQ location
          this.model.lamCustomerCountry = countrycountryHeadQuarter;
        } else if (customerCountries?.length === 1) {
          // auto-select LAM customer country when there is only one
          this.model.lamCustomerCountry = customerCountries[ 0 ];
        }

        return customerCountries;
      });
  }

  private getOemCustomerTypes(oemId:number):void {
    if (oemId) {
      this.formlyService.fillField(this.oemCustomerTypeField, this.companyDataService.getOEMCustomerTypes(oemId))
        .then((customerTypes:ICodeDescription[]) => {
          this.oemCustomerTypeField.templateOptions.hide = !customerTypes.length;
        });
    }
  }

  private findCategoryByFieldRef(column:number, ref:string):ICollapsibleWebshopCategory {
    return _.filter(this.formSections?.[column], (category:ICollapsibleWebshopCategory) => category.fieldRef === ref)[0];
  }

  public onCategoryCollapsed(category:ICollapsibleWebshopCategory, collapsed:boolean):void {
    category.collapsed = collapsed;
  }

  private onCatalogAvailableChange = (data:ILabelledValue):void => {
    this.catalogUploadDateField.templateOptions.required = data?.value;

    // change status text in category collapsed info
    this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.CATALOG).collapsedInfo.description = this.getCatalogCollapsedInfo();
  };

  private onUserAddressListAvailableChange = (data:ILabelledValue):void => {
    this.userUploadDateField.templateOptions.required = data?.value;
  };

  private onWebshopStatusChange = (status:ILov):void => {
    if (!status) {
      return;
    }

    // change status text in category collapsed info
    this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.WEBSHOP_STATUS).collapsedInfo.title = this.translationService.instant('WEBSHOP.WEBSHOP_INFO.STATUS_INFORMATION',
      {
        status: status.value.toUpperCase(),
        date: this.formattingService.formatDate(this.model.webshopStatusDate, 'DD MMM YYYY')
      });
  };

  private onWebshopStatusCommentsChange = (comments:string):void => {
    this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.WEBSHOP_STATUS).collapsedInfo.description = comments;
  };

  private onLAMChange = ():void => {
    setTimeout(() => { // give some time to formControl to set correct flag states

      // when LAM is becoming inactive and not returned in the list of options, the main LAM contacts must not be cleared out
      // however when the user changes manually the LAM field, the LAM contacts have to be cleared out
      const isUserChange:boolean = this.lamTypeField?.formControl?.dirty || this.lamCompanyCountryField?.formControl?.dirty || this.lamCompanyDepartmentField?.formControl?.dirty || this.lamCustomerCountryField?.formControl?.dirty;

      if (isUserChange) {
        this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.LAM).collapsedInfo.roleContacts = undefined;
        this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.LAM).collapsedInfo.contacts = undefined;
        this.model.lamRoleContacts = undefined;
        this.model.orderUpdateRoleContacts = undefined;

        this.form.patchValue({
          lamMainContacts: undefined,
          lamCustomerRelation: undefined,
          lamComments: undefined,
          activeCustomers: undefined,
          totalCustomers: undefined,
          superLAM: undefined,
          orderUpdateContacts: undefined
        });
      }

      this.webshopService.getLAM(this.model, this.webshopInfoData.customerInfo.company?.id)
        .then((lam:ICompanyCountry) => {
          const lamId:number = lam?.id;

          // do not disable LAM contacts field when LAM company is not active in db, but disable LAM contacts field when LAM field is not filled after a user change
          this.lamMainContactsField.templateOptions.disabled = isUserChange? !(this.lamType && lamId) : !this.lamType;
          if (this.orderUpdateContactsField?.templateOptions) {
            this.orderUpdateContactsField.templateOptions.disabled = !(this.lamType && lamId);
          }

          // change lam text in category collapsed info
          this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.LAM).collapsedInfo.title = lam?.company?.name;

          if (this.lamType && lamId) {
            this.companyDataService.getSuperLAM(this.lamType, lamId)
              .then((superLAM:IContact) => {
                this.form.patchValue({ superLAM });

                this.setGBDMBasedOnLAM();
              });

            this.companyDataService.getCompanyCustomerInfo(this.lamType, lamId)
              .then((customerData:ICompanyCustomerInfo) => {
                this.model.totalCustomers = customerData.totalCustomers;
                this.model.activeCustomers = customerData.activeCustomers;
              });
          } else {
            this.setGBDMBasedOnLAM();
          }
        });

    }, 50);
  };


  private onLAMTypeChange = ():void => {
    this.form.patchValue({
      lamCompanyCountry: undefined,
      lamCompanyDepartment: undefined,
      lamCustomerCountry: undefined,
      lamCustomerRelation: undefined,
      lamComments: undefined,
      activeCustomers: undefined,
      totalCustomers: undefined,
      superLAM: undefined,
      gbdm: undefined,
      orderUpdateContacts: undefined
    });

    this.model.lamRoleContacts = undefined;
    this.model.orderUpdateRoleContacts = undefined;
    this.model.orderUpdateContacts = undefined;

    this.webshopService.getLAM(this.model, this.webshopInfoData.customerInfo.company?.id)
      .then((lam:ICompanyCountry) => {
        const lamId:number = lam?.id;
        this.lamMainContactsField.templateOptions.disabled = !(this.lamType && lamId);
        this.orderUpdateContactsField.templateOptions.disabled = !(this.lamType && lamId);
      });

    let fillLAMFieldPromise:Promise<any>;
    switch (this.lamType) {
      case CompanyType.BP:
        fillLAMFieldPromise = this.formlyService.fillField(this.lamCompanyCountryField, this.lamCompanyCountriesPromise());
        break;
      case CompanyType.OEM:
        fillLAMFieldPromise = this.formlyService.fillField(this.lamCompanyDepartmentField, this.lamCompanyDepartmentsPromise());
        break;
      case CompanyType.CUSTOMER:
        fillLAMFieldPromise = this.formlyService.fillField(this.lamCustomerCountryField, this.lamCustomerCountriesPromise());
        break;
    }

    fillLAMFieldPromise
      .then(() => {
        // for some scenarios LAM field is already filled in at this moment -> trigger change to populate the dependent fields
        this.onLAMChange();
      });
  };

  private onLAMMainContactsChange = (newContacts?:IContact[]):void => {
    newContacts = newContacts || this.model.lamMainContacts;

    // this makes difference between initial loaded roles and the modified contacts and returns 2 objects: roleContacts (contacts already assigned to a role) and contacts (new contacts not assigned to a role yet)
    const diff:IObject = this.webshopInfoService.diffContactsInRoles(newContacts, RoleContactType.LAM);

    // change contact text in category collapsed info
    this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.LAM).collapsedInfo.roleContacts = diff.roleContacts;
    this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.LAM).collapsedInfo.contacts = diff.contacts;

    this.lamMainContactsInfoField.templateOptions.inputs = {
      roleContacts: diff.roleContacts,
      contacts: diff.contacts
    };
  };

  private onMainOEMChange = (isMainOem:boolean):void => {
    this.oemField.templateOptions.disabled = !isMainOem;
    this.oemField.templateOptions.required = isMainOem;

    if (util.notNullOrUndefinedOrEmpty(isMainOem) && !isMainOem && this.oemField?.formControl) {
      this.oemField.formControl.patchValue(undefined);

      // trigger oem change
      this.onOEMChange();
    }
  };

  private onOEMChange = (oem?:ICompany):void => {
    this.oemMainContactsField.templateOptions.disabled = !oem;
    this.customerIdOemField.templateOptions.disabled = !oem;
    this.oemCustomerTypeField.templateOptions.disabled = !oem;

    if (this.model && !oem) {
      this.model.oemRoleContacts = undefined;
      this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.OEM).collapsedInfo.roleContacts = undefined;
      this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.OEM).collapsedInfo.contacts = undefined;

      this.oemMainContactsField.templateOptions.info = undefined;
      this.form.patchValue({
        oemMainContacts: undefined,
        oemCustomerType: undefined,
        customerIdWithOem: undefined
      });
    }

    // change oem text in category collapsed info
    this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.OEM).collapsedInfo.title = oem?.name;

    // fetch oem customer types
    this.getOemCustomerTypes(oem?.id);
  };

  private onOEMMainContactsChange = (newContacts?:IContact[]):void => {
    newContacts = newContacts || this.model.oemMainContacts;

    // this makes difference between initial loaded roles and the modified contacts and returns 2 objects: roleContacts (contacts already assigned to a role) and contacts (new contacts not assigned to a role yet)
    const diff:IObject = this.webshopInfoService.diffContactsInRoles(newContacts, RoleContactType.VGAM);

    // change contact text in category collapsed info
    this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.OEM).collapsedInfo.roleContacts = diff.roleContacts;
    this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.OEM).collapsedInfo.contacts = diff.contacts;

    this.oemMainContactsInfoField.templateOptions.inputs = {
      roleContacts: diff.roleContacts,
      contacts: diff.contacts
    };
  };

  private onCustomerMainContactChange = (contact:IContact):void => {
    // change contact text in category collapsed info
    const diff: IObject = this.webshopInfoService.diffContactsInRoles([contact], RoleContactType.CGAM);
    this.findCategoryByFieldRef(FieldColumn.LEFT, FieldRef.CUSTOMER).collapsedInfo.roleContacts = diff.roleContacts;
    this.findCategoryByFieldRef(FieldColumn.LEFT, FieldRef.CUSTOMER).collapsedInfo.contacts = diff.contacts;

    this.customerMainContactInfoField.templateOptions.inputs = {
      roleContacts: diff.roleContacts,
      contacts: diff.contacts
    };
  };

  private onGBDMContactChange = (contact:IContact):void => {
    this.model.gbdmCompany = contact?.company;

    const diff: IObject = this.webshopInfoService.diffContactsInRoles([contact], RoleContactType.GBDM);

    // change contact text in category collapsed info
    this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.ACSL).collapsedInfo.roleContacts = diff.roleContacts;
    this.findCategoryByFieldRef(FieldColumn.RIGHT, FieldRef.ACSL).collapsedInfo.contacts = diff.contacts;

    if (this.canUpdateGBDM) {
      this.gbdmContactInfoField.templateOptions.inputs = {
        roleContacts: diff.roleContacts,
        contacts: diff.contacts
      };
    }
  };

  private getFieldType(type:string):string {
    return this.hasWritePermission? type : FormlyType.VALUE;
  }

  private createCustomerFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aValue('customerCompany')
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.SEARCH), 'customerMainContact')
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .withFormat(this.formattingService.formatContact)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string) => this.webshopDataService.getWebshopContacts(this.webshopService.getWebshopId(), RoleContactType.LAM, filter)
            },
            minSearchLength: 2
          }
        })
        .onChange(this.onCustomerMainContactChange)
        .build(),

      this.customerMainContactInfoField = this.getContactInfoField(FieldColumn.LEFT, FieldRef.CUSTOMER),

      this.companyHQField = this.formlyService.aField(this.getFieldType(FormlyType.SEARCH), 'customerCompanyHQ')
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .withFormat(this.formattingService.formatLocationHtml)
        .withExtraOptions({
          templateOptions: {
            localSearch: true,
            parseHtml: true,
            minSearchLength: 1
          }
        })
        .onChange((location:ILocation) => this.model.addressHQ = location)
        .build(),
      this.formlyService.aValue('addressHQ')
        .withoutEditable()
        .withFormat(this.formattingService.formatLocationHtml)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(`${this.valueClass.leftColumn} no-padding-top`)
        .withWrapperClass('info-form')
        .withHideExpression(() => !this.model || !this.model.customerCompanyHQ || !this.hasWritePermission)
        .build(),
      this.customerSectorField = this.formlyService.aField(this.getFieldType(FormlyType.SELECT), 'customerSector')
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .withFormat(this.formattingService.formatDescription)
        .withExtraOptions({
          templateOptions: {
            addInitialOption: false,
            trackBy: 'code'
          }
        })
        .build(),
      this.formlyService.aTextarea('customerSectorComment')
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.INPUT), 'customerHomepage')
        .withMaxLength(300)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aGroup('WEBSHOP.WEBSHOP_INFO.LOGO.LABEL')
        .withEditable('LOGO')
        .withFields([
          this.formlyService.aCustom() // customer logo
            .withComponent(ImageUploadComponent)
            .withoutWrapper()
            .withWrapperClass(this.fullColumnClass)
            .withExtraOptions({
              templateOptions: {
                inputs: {
                  textMissingImage: 'WEBSHOP.NO_LOGO',
                  url: this.getCustomerLogoUrl(),
                  hasImage: this.webshopInfoData?.customerInfo?.hasCustomerLogo,
                  hasWritePermission: this.hasWritePermission,
                  textDeleteImage: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.CONFIRM_DELETE_LOGO')
                }
              }
            })
            .build()
        ])
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.CHECKBOX), 'spotlight')
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build()
    ];
  }

  private createWebshopFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aField(this.canUpdateWebshopNameAndType? this.getFieldType(FormlyType.INPUT): this.getFieldType(FormlyType.VALUE), 'webshopName')
        .withRequired(this.hasWritePermission && this.canUpdateWebshopNameAndType)
        .withMaxLength(100)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .onChange((name:string) => this.findCategoryByFieldRef(FieldColumn.LEFT, FieldRef.WEBSHOP).collapsedInfo.title = name)
        .build(),
      this.formlyService.aGroup()
        .withEditable('INPRODUCTION_GROUP')
        .withFields([
          this.formlyService.aCheckbox('inProduction')
            .withoutEditable()
            .withWrapperClass(this.fullColumnClass)
            .withLabelClass('d-none')
            .onChange((isProduction:boolean) => this.findCategoryByFieldRef(FieldColumn.LEFT, FieldRef.WEBSHOP).collapsedInfo.testMode = !isProduction)
            .withHideExpression(() => !this.hasWritePermission)
            .build(),
          // this will show when user doesn't have write permission
          this.formlyService.aValue('inProduction')
            .withoutEditable()
            .withFormat(this.formattingService.formatBoolean)
            .withoutWrapper()
            .withValueClass(this.fullColumnClass)
            .withHideExpression(() => this.hasWritePermission)
            .build(),
          this.formlyService.aTemplate(`
            <div class="col-md-12">
              <div class="alert alert-warning no-margin">
                <i class="fa fa-warning"></i>
                ${this.translationService.instant('WEBSHOP.WEBSHOP_INFO.TEST_MODE.TEXT')}
              </div>
            </div>
          `)
            .withWrapperClass('col-md-12 double-space-top')
            .withHideExpression(() => this.model.inProduction)
            .build()
        ])
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.TEXTAREA), 'webshopDescription')
        .withMaxLength(2000)
        .withLabelClass(this.fullColumnClass)
        .withValueClass(this.fullColumnClass)
        .onChange((description:string) => this.findCategoryByFieldRef(FieldColumn.LEFT, FieldRef.WEBSHOP).collapsedInfo.description = description)
        .build(),
      this.webshopTypeField = this.formlyService.aField(this.canUpdateWebshopNameAndType? this.getFieldType(FormlyType.SELECT): this.getFieldType(FormlyType.VALUE), 'webshopType')
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .withFormat(this.formattingService.formatLov)
        .build(),
      this.orderProcessField = this.formlyService.aField(this.getFieldType(FormlyType.SELECT), 'orderProcess')
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aGroup()
        .withEditable('PRODUCTPROCUREMENT_GROUP')
        .withFields([
          this.formlyService.aCheckbox('productProcurement')
            .withoutEditable()
            .withWrapperClass(this.fullColumnClass)
            .withoutWrapper()
            .withHideExpression(() => !this.hasWritePermission)
            .build(),
          this.formlyService.aValue('productProcurement')
            .withoutEditable()
            .withFormat(this.formattingService.formatBoolean)
            .withoutWrapper()
            .withValueClass(this.fullColumnClass)
            .withHideExpression(() => this.hasWritePermission)
            .build()
        ])
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aGroup()
        .withEditable('LOCALSERVICERFP_GROUP')
        .withFields([
          this.formlyService.aCheckbox('localServiceRFP')
            .withoutEditable()
            .withWrapperClass(this.fullColumnClass)
            .withoutWrapper()
            .withHideExpression(() => !this.hasWritePermission)
            .build(),
          this.formlyService.aValue('localServiceRFP')
            .withoutEditable()
            .withoutWrapper()
            .withValueClass(this.fullColumnClass)
            .withFormat(this.formattingService.formatBoolean)
            .withHideExpression(() => this.hasWritePermission)
            .build()
        ])
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aGroup('WEBSHOP.WEBSHOP_INFO.LOGO.LABEL')
        .withEditable('LOGO')
        .withFields([
          this.formlyService.aCustom() // webshop logo
            .withComponent(ImageUploadComponent)
            .withoutWrapper()
            .withWrapperClass(this.fullColumnClass)
            .withExtraOptions({
              templateOptions: {
                inputs: {
                  textMissingImage: 'WEBSHOP.NO_LOGO',
                  url: this.getWebshopLogoUrl(),
                  hasImage: this.webshopInfoData?.customerInfo?.hasWebshopLogo,
                  hasWritePermission: this.hasWritePermission,
                  textDeleteImage: this.translationService.instant('WEBSHOP.WEBSHOP_INFO.CONFIRM_DELETE_LOGO')
                }
              }
            })
            .build()
        ])
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build()
    ];
  }

  private createAssessmentFields():IExtraFieldConfigurationObject[] {
    const onChangeHandler = () => this.findCategoryByFieldRef(FieldColumn.LEFT, FieldRef.ASSESSMENT).collapsedInfo.description = this.getAssessmentCollapsedInfo();

    return [
      this.opportunityTypeField = this.formlyService.aField(this.getFieldType(FormlyType.SELECT), 'opportunityType')
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.winChancesField = this.formlyService.aField(this.getFieldType(FormlyType.SELECT), 'winChances')
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.INPUT), 'competitors')
        .withMaxLength(255)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.INPUT), 'detractors')
        .withMaxLength(255)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.INPUT), 'incumbentOemPartner')
        .withMaxLength(255)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.leftColumn)
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.DATE_INPUT), 'expectedDecisionDate')
        .withRequired(this.hasWritePermission)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.halfColumn)
        .withFormat((value:string) => this.hasWritePermission? value : this.formattingService.formatDate(value, 'DD MMM YYYY'))
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.DATE_INPUT), 'expectedStartDate')
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.halfColumn)
        .withFormat((value:string) => this.hasWritePermission? value : this.formattingService.formatDate(value, 'DD MMM YYYY'))
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.INPUT), 'expectedOrdersPerYear')
        .withInputType('number')
        .withMaxLength(9)
        .withPattern(/^\d*$/)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.halfColumn)
        .withExtraOptions({
          templateOptions: {
            min: 0
          }
        })
        .onChange(onChangeHandler)
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.INPUT), 'revenueForecast')
        .withRequired(this.hasWritePermission)
        .withInputType('number')
        .withPattern(/^\d*$/)
        .withMaxLength(9)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.halfColumn)
        .withAddOn(this.hasWritePermission? 'USD' : undefined)
        .withFormat((value:number) => this.hasWritePermission? value : this.formattingService.formatAmount(value, 'USD'))
        .withExtraOptions({
          templateOptions: {
            min: 0
          }
        })
        .onChange(onChangeHandler)
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.SELECT), 'userAddressListAvailable')
        .withFormat(this.formattingService.formatLabel)
        .withOptions(this.webshopInfoService.getBooleanValues())
        .withExtraOptions({
          templateOptions: {
            trackBy: 'value'
          }
        })
        .onChange(this.onUserAddressListAvailableChange)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.halfColumn)
        .build(),
      this.userUploadDateField = this.formlyService.aField(this.getFieldType(FormlyType.DATE_INPUT), 'expectedUserUploadDate')
        .withRequired(this.hasWritePermission)
        .withLabelClass(this.labelClass.leftColumn)
        .withValueClass(this.valueClass.halfColumn)
        .withFormat((value:string) => this.hasWritePermission? value : this.formattingService.formatDate(value, 'DD MMM YYYY'))
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.TEXTAREA), 'userListUploadComment')
        .withMaxLength(100)
        .withLabelClass(this.fullColumnClass)
        .withValueClass(this.fullColumnClass)
        .build()
    ];
  }

  private createAccountManagementFields():IExtraFieldConfigurationObject[] {
    const fields:IExtraFieldConfigurationObject[] = [
      this.focusContactTypeField = this.formlyService.aField(this.canUpdateAccountManagement? FormlyType.SELECT : FormlyType.VALUE, 'focusContactType')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formattingService.formatLov)
        .onChange(() => this.fillFocusContactField())
        .build(),
      this.focusContactInfoField = this.formlyService.aCustom()
        .withComponent(ListContactsComponent)
        .withWrapperClass('col-12 role-contact underneath')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withHideExpression(() => !this.model.focusContactType || !this.model.focusContact?.length)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              roleContacts: this.model.focusContact
            }
          }
        })
        .build(),
      this.orderManagementTypeField = this.formlyService.aField(this.getFieldType(this.canUpdateAccountManagement? FormlyType.SELECT : FormlyType.VALUE), 'orderManagementType')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formattingService.formatLov)
        .build(),

      this.formlyService.aCustom()
        .withComponent(FaqPopupComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              faqCode: FAQ_ORDER_MANAGEMENT_TYPES,
              label: `<span class="info">${this.translationService.instant('WEBSHOP.WEBSHOP_INFO.ORDERMANAGEMENTTYPE.LABEL')}</span> <i class="fa fa-info-circle info-icon"></i>`
            }
          }
        })
        .withoutWrapper()
        .build(),

      this.canUpdateAccountManagement? this.orderUpdateContactsField = this.formlyService.aMultiSelect(ORDER_UPDATE_CONTACTS_KEY)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string) => {
                return this.webshopService.getLAM(this.model, this.webshopInfoData.customerInfo.company?.id)
                  .then((lam:ICompanyCountry) => this.companyDataService.getLAMMainContacts(filter, this.lamType, lam?.id, RoleContactType.ORDER_UPDATE));
              }
            },
            minSearchLength: 1,
            // the option to create a new contact dynamically shows/hides when the multilist is open
            onOpen: this.addRemoveOptionCreateOrderUpdateContacts
          }
        })
        .withFormat(this.formattingService.formatContact)
        .onChange(this.onOrderUpdateContactsChange)
        .build() : {},

      this.orderUpdateContactsInfoField = this.formlyService.aCustom(!this.canUpdateAccountManagement? 'WEBSHOP.WEBSHOP_INFO.ORDERUPDATECONTACTS.LABEL' : '')
        .withComponent(ListContactsComponent)
        .withWrapperClass(`col-12 role-contact ${this.canUpdateAccountManagement? 'underneath' : ''}`)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withHideExpression(() => !this.model.orderUpdateContacts?.length)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              roleContacts: this.findCategoryByFieldRef(FieldColumn.LEFT, FieldRef.ACCOUNT_MANAGEMENT)?.collapsedInfo?.orderUpdateRoleContacts,
              contacts: this.findCategoryByFieldRef(FieldColumn.LEFT, FieldRef.ACCOUNT_MANAGEMENT)?.collapsedInfo?.orderUpdateContacts
            }
          }
        })
        .build(),

      this.amTypeField = this.formlyService.aField(this.canUpdateAccountManagement? FormlyType.SELECT : FormlyType.VALUE, 'amType')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formattingService.formatLov)
        .build(),
      this.formlyService.aField(this.getFieldType(this.canUpdateAccountManagement? FormlyType.INPUT : FormlyType.VALUE), 'amTime')
        .withMaxLength(500)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .build(),
      this.formlyService.aField(this.getFieldType(this.canUpdateAccountManagement? FormlyType.INPUT : FormlyType.VALUE), 'timeSlot')
        .withMaxLength(500)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .build()
    ];

    return this.formlyService.removeEmptyFields(fields);
  }

  private addRemoveOptionCreateOrderUpdateContacts = ():void => {
    if (this.canCreateLam) {
      this.orderUpdateContactsField.templateOptions.addFunction = {
        label: 'WEBSHOP.CREATE_NEW_CONTACT',
        onClick: (searchTerm:string) => {
          this.webshopService.getLAM(this.model, this.webshopInfoData.customerInfo.company?.id)
            .then((lam:ICompanyCountry) => {
              return this.createNewContactDialog(searchTerm, lam);
            });
        }
      };
    } else {
      delete this.orderUpdateContactsField.templateOptions.addFunction;
    }
  };

  private createNewContactDialog = (searchTerm:string, lam?:ICompanyCountry):void => {
    // function to be passed to userprofile service for selecting a generic user
    // when LAM type is BP -> select a normal BP as generic user
    // when LAM type is CUSTOMER -> select a Customer Super User as generic user
    let genericUserFn:Function;
    if (this.lamType === CompanyType.BP || this.lamType === CompanyType.CUSTOMER) {
      genericUserFn = (genericUsers:IGenericUser[]) => {
        return _.find(genericUsers, (gu:IGenericUser) => gu.externalGroup?.code === (this.lamType === CompanyType.BP? 'PARTNERS' : 'CUSTOMER_SUPER_USER'));
      };
    }

    const closeDialog:Function = () => {
      this.userProfileDialog.hide();
    };

    // binding: onSaved
    const onSaved = (userData:IUserData):void => {
      const orderUpdateContacts:IContact[] = this.model[ ORDER_UPDATE_CONTACTS_KEY ] || [];
      orderUpdateContacts.push(<IContact>userData);
      this.form.patchValue({
        [ ORDER_UPDATE_CONTACTS_KEY ]: orderUpdateContacts
      });
      closeDialog();
    };

    // binding: onCancel
    const onCancel = ():void => {
      closeDialog();
    };

    this.userProfileDialog = this.messageService.showDialog({
      component: UserProfileWrapperComponent,
      wrapperComponent: false,
      inputs: {
        userId: 'NEW',
        companyId: lam?.company?.id,
        countryId: lam?.country?.id,
        firstName: !/\s/.test(searchTerm)? util.capitalize(searchTerm) : undefined,
        genericUserFn
      },
      outputs: {
        onSaved,
        onCancel
      },
      hideHeader: true,
      hideFooter: true,
      size: DialogSize.XL
    });
  };

  private onOrderUpdateContactsChange = (newContacts?:IContact[]):void => {
    newContacts = newContacts || this.model.orderUpdateContacts;

    // this makes difference between initial loaded roles and the modified contacts and returns 2 objects: roleContacts (contacts already assigned to a role) and contacts (new contacts not assigned to a role yet)
    const diff:IObject = this.webshopInfoService.diffContactsInRoles(newContacts, RoleContactType.ORDER_UPDATE);

    // change contact text in category collapsed info
    this.findCategoryByFieldRef(FieldColumn.LEFT, FieldRef.ACCOUNT_MANAGEMENT).collapsedInfo.orderUpdateRoleContacts = diff.roleContacts;
    this.findCategoryByFieldRef(FieldColumn.LEFT, FieldRef.ACCOUNT_MANAGEMENT).collapsedInfo.orderUpdateContacts = diff.contacts;

    this.orderUpdateContactsInfoField.templateOptions.inputs = {
      roleContacts: diff.roleContacts,
      contacts: diff.contacts
    };
  };

  private createWebshopStatusFields():IExtraFieldConfigurationObject[] {
    return [
      this.webshopStatusField = this.formlyService.aField(this.getFieldType(FormlyType.SELECT), FieldRef.WEBSHOP_STATUS)
        .withRequired(this.hasWritePermission)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formattingService.formatLov)
        .onChange(this.onWebshopStatusChange)
        .build(),
      this.formlyService.aValue('webshopStatusDate')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.halfColumn)
        .withFormat((value:string) => this.formattingService.formatDate(value, 'DD MMM YYYY'))
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.TEXTAREA), 'webshopStatusComments')
        .withMaxLength(1000)
        .withLabelClass(this.fullColumnClass)
        .withValueClass(this.fullColumnClass)
        .onChange(this.onWebshopStatusCommentsChange)
        .build(),
      this.formlyService.aValue('webshopLastUpdateComments')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.halfColumn)
        .withFormat((value:string) => this.formattingService.formatDate(value, 'DD MMM YYYY'))
        .build()
    ];
  }

  private createOEMFields():IExtraFieldConfigurationObject[] {
    if (this.hasWritePermission) {
      this.oemMainContactsField = this.formlyService.aMultiSelect('oemMainContacts')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formattingService.formatContact)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string):Promise<IContact[]> => {
                return this.model.oem?.id? this.companyDataService.getVGAMContacts(filter, this.model.oem.id) : Promise.resolve([]);
              }
            },
            minSearchLength: 1
          }
        })
        .onChange(this.onOEMMainContactsChange)
        .withDisabled(false)
        .build();
    } else {
      this.oemMainContactsField = this.formlyService.aValue('oemMainContacts')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formatMultipleContacts)
        .build();
    }

    return [
      this.formlyService.aGroup()
        .withEditable('MAINOEM')
        .withFields([
          this.formlyService.aCheckbox('isMainOem')
            .withoutEditable()
            .withWrapperClass(this.fullColumnClass)
            .withoutWrapper()
            .onChange(this.onMainOEMChange)
            .withHideExpression(() => !this.hasWritePermission)
            .build(),

          this.formlyService.aValue('isMainOem')
            .withoutEditable()
            .withoutWrapper()
            .withValueClass(this.fullColumnClass)
            .withFormat(this.formattingService.formatBoolean)
            .withHideExpression(() => this.hasWritePermission)
            .build()
        ])
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .build(),
      this.oemField = this.formlyService.aField(this.getFieldType(FormlyType.SEARCH), FieldRef.OEM)
        .withRequired(this.hasWritePermission)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formattingService.formatCompany)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (name:string) => this.companyDataService.getVendorCompanies(true, name)
            },
            minSearchLength: 1
          }
        })
        .onChange(this.onOEMChange)
        .build(),

      this.oemMainContactsField,
      this.oemMainContactsInfoField = this.getContactInfoField(FieldColumn.RIGHT, FieldRef.OEM),

      this.customerIdOemField = this.formlyService.aField(this.getFieldType(FormlyType.INPUT), 'customerIdWithOem')
        .withMaxLength(100)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withDisabled(false)
        .build(),
      this.oemCustomerTypeField = this.formlyService.aField(this.getFieldType(FormlyType.SELECT), 'oemCustomerType')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formattingService.formatDescription)
        .withDisabled(false)
        .build()
    ];
  }

  private createCatalogFields(): IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aField(this.getFieldType(FormlyType.SELECT), 'oemCatalogAvailable')
        .withRequired(this.hasWritePermission)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.halfColumn)
        .withFormat(this.formattingService.formatLabel)
        .withExtraOptions({
          templateOptions: {
            trackBy: 'value'
          }
        })
        .withOptions(this.webshopInfoService.getBooleanValues())
        .onChange(this.onCatalogAvailableChange)
        .build(),
      this.catalogUploadDateField = this.formlyService.aField(this.getFieldType(FormlyType.DATE_INPUT), 'expectedCatalogUploadDate')
        .withRequired(this.hasWritePermission)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.halfColumn)
        .withFormat((value:string) => this.hasWritePermission? value : this.formattingService.formatDate(value, 'DD MMM YYYY'))
        .build(),
      this.formlyService.aField(this.getFieldType(FormlyType.TEXTAREA), 'catalogUploadComment')
        .withMaxLength(100)
        .withLabelClass(this.fullColumnClass)
        .withValueClass(this.fullColumnClass)
        .build(),
      this.formlyService.aTitle('Local BP Catalogs')
        .build(),
      this.formlyService.aValue('bpCatalogActivatedQty')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.halfColumn)
        .build(),
      this.formlyService.aValue('bpCatalogNotActivatedQty')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.halfColumn)
        .build(),
      this.formlyService.aGroup()
        .withFields([
          this.formlyService.aLink('WEBSHOP.WEBSHOP_INFO.BUTTON_MANAGE_CATALOGS')
            .withoutWrapper()
            .withWrapperClass('col-12')
            .withExtraOptions({
              templateOptions: {
                linkClass: 'btn btn-primary'
              }
            })
            .withHideExpression(() => !this.hasWritePermission)
            .withUrl(`/ga2/#/lists/CATALOG_SELECTION?showfilters=true&autoload=true&filters=Webshop_id%09${this.webshopService.getWebshopId()}&menu=WEBSHOP_${this.webshopService.getWebshopId()}`)
            .build()
        ])
        .withoutWrapper()
        .build()
    ];
  }

  private createLAMFields():IExtraFieldConfigurationObject[] {
    if (this.hasWritePermission) {
      this.lamMainContactsField = this.formlyService.aMultiSelect('lamMainContacts')
        .isRequired()
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formattingService.formatContact)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string) => {
                return this.webshopService.getLAM(this.model, this.webshopInfoData.customerInfo.company?.id)
                  .then((lam:ICompanyCountry) => this.companyDataService.getLAMMainContacts(filter, this.lamType, lam?.id, RoleContactType.LAM));
              }
            },
            minSearchLength: 1
          }
        })
        .onChange(this.onLAMMainContactsChange)
        .build();
    } else {
      this.lamMainContactsField = this.formlyService.aValue('lamMainContacts')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formatMultipleContacts)
        .build();
    }

    return [
      this.lamTypeField = this.formlyService.aField(this.canUpdateLamType? FormlyType.RADIO : FormlyType.VALUE, 'lamType')
        .withFormat(this.formattingService.formatLov)
        .withWrapperClass('radio-horizontal')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withOptions(this.webshopInfoService.getLamTypes())
        .withHideExpression(() => !this.canViewLamType)
        .onChange((lamType:IValueObject, oldLamType:IValueObject) => {
          if (!_.isEqual(oldLamType, lamType)) { // detect if there is a real change made by user
            this.onLAMTypeChange();
          }
        })
        .build(),

      this.lamCompanyCountryField = this.formlyService.aField(this.hasWritePermission? FormlyType.SELECT : FormlyType.VALUE, 'lamCompanyCountry')
        .withRequired(this.hasWritePermission)
        .withFormat(this.formattingService.formatCompanyCountryL)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withHideExpression(() => !this.model.lamType || this.model.lamType.id !== CompanyType.BP)
        .withLoading(false)
        .onChange(this.onLAMChange)
        .build(),

      this.lamCompanyDepartmentField = this.formlyService.aField(this.hasWritePermission? FormlyType.SELECT : FormlyType.VALUE, 'lamCompanyDepartment')
        .withRequired(this.hasWritePermission)
        .withFormat(this.formattingService.formatCompanyCountryL)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withHideExpression(() => !this.model.lamType || this.model.lamType.id !== CompanyType.OEM)
        .withLoading(false)
        .onChange(this.onLAMChange)
        .build(),

      this.lamCustomerCountryField = this.formlyService.aField(this.hasWritePermission? FormlyType.SELECT : FormlyType.VALUE, 'lamCustomerCountry')
        .withRequired(this.hasWritePermission)
        .withFormat(this.formattingService.formatName)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withHideExpression(() => !this.model.lamType || this.model.lamType.id !== CompanyType.CUSTOMER)
        .withLoading(false)
        .onChange(this.onLAMChange)
        .build(),

      this.lamMainContactsField,
      this.lamMainContactsInfoField = this.getContactInfoField(FieldColumn.RIGHT, FieldRef.LAM),

      this.customerRelationField = this.formlyService.aField(this.getFieldType(FormlyType.SELECT), 'lamCustomerRelation')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formattingService.formatLov)
        .build(),

      this.formlyService.aField(this.getFieldType(FormlyType.TEXTAREA), 'lamComments')
        .withMaxLength(1000)
        .withLabelClass(this.fullColumnClass)
        .withValueClass(this.fullColumnClass)
        .build(),

      this.formlyService.aValue('activeCustomers')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .build(),

      this.formlyService.aValue('totalCustomers')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .build(),

      this.formlyService.aValue('superLAM')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat((companyCountry:ICompanyCountry) => companyCountry && `${companyCountry.company?.name} - ${companyCountry.country?.name}`)
        .build()
    ];
  }

  private getContactInfoField(column:number, ref:FieldRef):IExtraFieldConfigurationObject {
    const hideExpression = ():boolean => {
      switch (ref) {
        case FieldRef.ACSL:
          return !this.model.gbdm;
        case FieldRef.CUSTOMER:
          return !this.model.customerMainContact;
        case FieldRef.LAM:
          return !this.model.lamMainContacts?.length;
        case FieldRef.OEM:
          return !this.model.oemMainContacts?.length;
      }
    };

    return this.formlyService.aCustom()
      .withComponent(ListContactsComponent)
      .withWrapperClass('col-12 role-contact underneath')
      .withLabelClass(this.labelClass.rightColumn)
      .withValueClass(this.valueClass.rightColumn)
      .withHideExpression(hideExpression)
      .withExtraOptions({
        templateOptions: {
          inputs: {
            roleContacts: this.findCategoryByFieldRef(column, ref)?.collapsedInfo?.roleContacts,
            contacts: this.findCategoryByFieldRef(column, ref)?.collapsedInfo?.contacts
          }
        }
      })
      .build();
  }

  private createACSLFields():IExtraFieldConfigurationObject[] {
    const acslFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aValue('gbdmCompany')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .build(),

      this.gbdmContactField = this.formlyService.aField(this.canUpdateGBDM? FormlyType.SEARCH : FormlyType.VALUE, 'gbdm')
        .withRequired(this.canUpdateGBDM)
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formattingService.formatContact)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .onChange(this.onGBDMContactChange)
        .build()
    ];

    // when in read-only prevent showing twice the contact (aValue and contact-info)
    if (this.canUpdateGBDM) {
      acslFields.push(this.gbdmContactInfoField = this.getContactInfoField(FieldColumn.RIGHT, FieldRef.ACSL));
    }

    acslFields.push(
      this.formlyService.aField(this.canUpdateGBDM? FormlyType.DATE_INPUT : FormlyType.VALUE, 'expectedDeskTransferDate')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat((value:string) => this.canUpdateGBDM? value : this.formattingService.formatDate(value, 'DD MMM YYYY'))
        .build()
    );

    return acslFields;
  }

  private formatMultipleContacts = (contacts:IContact[]):string => {
    let formatted:string = '';

    for (let i = 0; i < contacts.length; i++) {
      if (contacts[ i ].firstname) {
        formatted += contacts[ i ].firstname;
      }
      if (contacts[ i ].lastname) {
        formatted += `${contacts[ i ].firstname? ' ' : ''}${contacts[ i ].lastname}`;
      }
      if (contacts[ i ].email) {
        formatted += `<br>${contacts[ i ].email}`;
      }
      if (contacts[ i ].title) {
        formatted += `<br>${contacts[ i ].title}`;
      }

      formatted += '<div class="col-space-bottom">&nbsp;</div>';
    }

    return formatted;
  };

  private expandAllCategories = ():void => {
    _.each(this.formSections, (categories:ICollapsibleWebshopCategory[]) => {
      _.forEach(categories, (category:ICollapsibleWebshopCategory) => {
        category.collapsed = false;
      });
    });
  };

  public validate = ():Promise<void|string[]> => {
    const fields:IExtraFieldConfigurationObject[] = [];

    _.each(this.formSections, (categories:ICollapsibleWebshopCategory[]) => {
      _.each(categories, (category:ICollapsibleWebshopCategory) => {
        fields.push(...category.fields);
      });
    });

    return this.validateService.validateFields(
      fields,
      this.model,
      true
    );
  };

  private saveWebshopInfo = ():void => {
    this.wizard.validateCurrent()
      .then(() => this.webshopService.getLAM(this.model, this.webshopInfoData.customerInfo.company?.id))
      .then((lam:ICompanyCountry) => {
        const requestData:IWebshopInfoData = this.webshopInfoService.createWebshopInfoRequest(this.webshopInfoData, this.model, lam);
        this.webshopService.updateWizardButtonStatus(true);

        this.webshopInfoDataService.saveWebshopInfo(this.webshopService.getWebshopId(), requestData)
          .then((data:IWebshopInfoData) => {
            this.webshopInfoData = data;

            // init formly model
            this.model = this.webshopInfoService.createWebshopInfoModel(this.webshopInfoData);
            this.fillFocusContactField();

            // init categories & apply changes to view screen
            this.initCategories();
            this.loadFocusContact();
            this.onCustomerMainContactChange(this.model.customerMainContact);
            this.onGBDMContactChange(this.model.gbdm);

            this.messageService.showFeedbackAfterSave('WEBSHOP.TABS.WEBSHOP_INFO');

            if (this.webshopService.onSaved) {
              this.webshopService.onSaved.emit(this.webshopService.getWebshopId());
            }
          })
          .finally(() => {
            this.webshopService.updateWizardButtonStatus(false);
          });
      })
      .catch(() => {
        this.expandAllCategories();
      });
  };

  private getCustomerLogoUrl():string {
    return `rest/compman/company/${this.webshopInfoData.customerInfo?.company?.id}/logo`;
  }

  private getWebshopLogoUrl():string {
    return `rest/webshops/${this.webshopInfoData.webshop?.id}/logo`;
  }

  public ngOnDestroy():void {
    // remove action button when wizard step leave from this component
    this.wizard.removeAction(SAVE_INFO_ACTION);
    this.wizard.clearCurrentValidator();

    // cancel running requests
    this.webshopInfoDataService.clear();

    // clear cache
    this.companyDataService.clear();
    this.webshopInfoService.clear();
    this.gaStateService.clear();
  }
}